export interface OperationalFactSource {
  readonly authority: string;
  readonly revision: string;
}

export interface OperationalFactInput {
  readonly eventId: string;
  readonly aggregateType: string;
  readonly aggregateId: string;
  readonly factType: string;
  readonly businessDate: string;
  readonly occurredAt: string;
  readonly recordedAt: string;
  readonly schemaVersion: number;
  readonly source: OperationalFactSource;
  readonly payload: unknown;
}

export type OperationalFact = OperationalFactInput;

export interface OwnerReadModel {
  readonly factCount: number;
  readonly countsByType: Readonly<Record<string, number>>;
  readonly latestOccurredAt: string | null;
  readonly latestRecordedAt: string | null;
  readonly businessDates: readonly string[];
  readonly sourceRevisionsByAuthority: Readonly<Record<string, readonly string[]>>;
}

export type OwnerCommandTarget = 'admin_control' | 'business_runtime';

export interface OwnerCommandInput {
  readonly commandId: string;
  readonly idempotencyKey: string;
  readonly actorId: string;
  readonly target: OwnerCommandTarget;
  readonly action: string;
  readonly payload: unknown;
  readonly createdAt: string;
}

export type OwnerCommand = OwnerCommandInput;

const clone = <T>(value: T): T => structuredClone(value);

const stableValue = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(stableValue);
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, entry]) => [key, stableValue(entry)]),
    );
  }
  return value;
};

const signatureOf = (value: unknown): string => JSON.stringify(stableValue(value));

const assertText = (value: string, code: string): void => {
  if (!value?.trim()) throw new Error(code);
};

const timestampMs = (value: string): number => {
  const parsed = Date.parse(value);
  if (!Number.isFinite(parsed)) throw new Error('OPERATIONAL_FACT_TIME_INVALID');
  return parsed;
};

const validateFact = (fact: OperationalFactInput): void => {
  assertText(fact.eventId, 'OPERATIONAL_FACT_ID_REQUIRED');
  assertText(fact.aggregateType, 'OPERATIONAL_FACT_AGGREGATE_TYPE_REQUIRED');
  assertText(fact.aggregateId, 'OPERATIONAL_FACT_AGGREGATE_ID_REQUIRED');
  assertText(fact.factType, 'OPERATIONAL_FACT_TYPE_REQUIRED');
  assertText(fact.businessDate, 'OPERATIONAL_FACT_BUSINESS_DATE_REQUIRED');
  if (!/^\d{4}-\d{2}-\d{2}$/.test(fact.businessDate)) throw new Error('OPERATIONAL_FACT_BUSINESS_DATE_INVALID');
  const occurredMs = timestampMs(fact.occurredAt);
  const recordedMs = timestampMs(fact.recordedAt);
  if (recordedMs < occurredMs) throw new Error('OPERATIONAL_FACT_RECORDED_BEFORE_OCCURRED');
  if (!Number.isSafeInteger(fact.schemaVersion) || fact.schemaVersion <= 0) {
    throw new Error('OPERATIONAL_FACT_SCHEMA_INVALID');
  }
  if (!fact.source || !fact.source.authority?.trim() || !fact.source.revision?.trim()) {
    throw new Error('OPERATIONAL_FACT_SOURCE_INVALID');
  }
};

export function createOperationalFacts() {
  const byId = new Map<string, { readonly signature: string; readonly fact: OperationalFact }>();
  const order: string[] = [];

  return {
    append(input: OperationalFactInput): OperationalFact {
      validateFact(input);
      const candidate = clone(input);
      const signature = signatureOf(candidate);
      const existing = byId.get(candidate.eventId);
      if (existing) {
        if (existing.signature !== signature) throw new Error('OPERATIONAL_FACT_ID_CONFLICT');
        return clone(existing.fact);
      }
      byId.set(candidate.eventId, { signature, fact: candidate });
      order.push(candidate.eventId);
      return clone(candidate);
    },

    get(eventId: string): OperationalFact | null {
      const stored = byId.get(eventId);
      return stored ? clone(stored.fact) : null;
    },

    all(): readonly OperationalFact[] {
      return order.map((eventId) => clone(byId.get(eventId)!.fact));
    },
  };
}

export function buildOwnerReadModel(facts: readonly OperationalFact[]): OwnerReadModel {
  const counts: Record<string, number> = {};
  const businessDates = new Set<string>();
  const sourceRevisions = new Map<string, Set<string>>();
  let latestOccurredAt: string | null = null;
  let latestRecordedAt: string | null = null;
  let latestOccurredMs = Number.NEGATIVE_INFINITY;
  let latestRecordedMs = Number.NEGATIVE_INFINITY;

  for (const fact of facts) {
    validateFact(fact);
    counts[fact.factType] = (counts[fact.factType] ?? 0) + 1;
    businessDates.add(fact.businessDate);
    const revisions = sourceRevisions.get(fact.source.authority) ?? new Set<string>();
    revisions.add(fact.source.revision);
    sourceRevisions.set(fact.source.authority, revisions);

    const occurredMs = Date.parse(fact.occurredAt);
    if (occurredMs > latestOccurredMs) {
      latestOccurredMs = occurredMs;
      latestOccurredAt = fact.occurredAt;
    }
    const recordedMs = Date.parse(fact.recordedAt);
    if (recordedMs > latestRecordedMs) {
      latestRecordedMs = recordedMs;
      latestRecordedAt = fact.recordedAt;
    }
  }

  const sourceRevisionsByAuthority = Object.fromEntries(
    [...sourceRevisions.entries()]
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([authority, revisions]) => [authority, Object.freeze([...revisions].sort())]),
  );

  return {
    factCount: facts.length,
    countsByType: Object.freeze({ ...counts }),
    latestOccurredAt,
    latestRecordedAt,
    businessDates: Object.freeze([...businessDates].sort()),
    sourceRevisionsByAuthority: Object.freeze(sourceRevisionsByAuthority),
  };
}

export function createOwnerCommand(input: OwnerCommandInput): OwnerCommand {
  assertText(input.commandId, 'OWNER_COMMAND_ID_REQUIRED');
  assertText(input.idempotencyKey, 'OWNER_COMMAND_IDEMPOTENCY_REQUIRED');
  assertText(input.actorId, 'OWNER_COMMAND_ACTOR_REQUIRED');
  if (input.target !== 'admin_control' && input.target !== 'business_runtime') {
    throw new Error('OWNER_COMMAND_TARGET_INVALID');
  }
  assertText(input.action, 'OWNER_COMMAND_ACTION_REQUIRED');
  if (!Number.isFinite(Date.parse(input.createdAt))) throw new Error('OWNER_COMMAND_TIME_INVALID');
  return clone(input);
}
